import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listAdCopyJobs, listAdCopyModels, enqueueAdCopyJob, retryAdCopyJobItem, cancelAdCopyJobItem,
  markAdCopyJobItemsLaunched,
  type AdCopyJob, type AdCopyLaunchSpec,
} from "../api/actions";
import { useToast } from "../components/ToastProvider";

export const AD_COPY_JOBS_KEY = ["ad_copy_jobs"] as const;

// Shared poll interval — mirrors usePipelineStatus's 20s cadence (useLauncher.ts). Any component can
// read the same cached list via useQuery(AD_COPY_JOBS_KEY, ...) without re-triggering its own poll;
// TanStack Query dedupes on the key.
const POLL_MS = 20_000;

export function useAdCopyJobsQuery() {
  return useQuery({
    queryKey: AD_COPY_JOBS_KEY,
    queryFn: () => listAdCopyJobs(),
    select: (d) => d.jobs,
    refetchInterval: POLL_MS,
  });
}

// The models a buyer may pick for ad copy (SOC-637). Changes only with a launcher deploy.
export function useAdCopyModels() {
  return useQuery({
    queryKey: ["ad_copy_models"],
    queryFn: () => listAdCopyModels(),
    staleTime: Infinity,
  });
}

// Queue ad-copy generation (bulk from the Launcher, or a single-item retry from the Review view via
// useRetryAdCopyJobItem instead — this one always creates a NEW job). Invalidates the shared poll key
// so the new job appears immediately rather than waiting up to POLL_MS for the next tick.
export function useEnqueueAdCopyJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, contentIds, launchSpec }:
      { templateId: number; contentIds: number[]; launchSpec?: AdCopyLaunchSpec }) =>
      enqueueAdCopyJob(templateId, contentIds, launchSpec),
    onSuccess: () => qc.invalidateQueries({ queryKey: AD_COPY_JOBS_KEY }),
  });
}

export function useMarkAdCopyJobItemsLaunched() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, itemIds }: { jobId: number; itemIds: number[] }) =>
      markAdCopyJobItemsLaunched(jobId, itemIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: AD_COPY_JOBS_KEY }),
  });
}

export function useRetryAdCopyJobItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, itemId }: { jobId: number; itemId: number }) => retryAdCopyJobItem(jobId, itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: AD_COPY_JOBS_KEY }),
  });
}

export function useCancelAdCopyJobItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, itemId }: { jobId: number; itemId: number }) => cancelAdCopyJobItem(jobId, itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: AD_COPY_JOBS_KEY }),
  });
}

/** Mounted ONCE at the app shell. Watches the shared poll for a queued/processing -> ready|partial|
 * error transition and fires the corresponding toast — this is what lets "your ads are ready"
 * appear no matter which view the buyer has navigated to since requesting generation. ALSO keeps a
 * persistent, live-updating toast up for as long as anything across any active job is still
 * queued/processing ("N processing · M ready"), since pushToast's one-shot stack-and-expire toasts
 * would otherwise say nothing while a batch is mid-generation. */
export function useAdCopyJobsWatcher() {
  const { data: jobs } = useAdCopyJobsQuery();
  const { pushToast, setPersistentToast } = useToast();
  const lastSeenStatus = useRef<Map<number, AdCopyJob["status"]>>(new Map());

  useEffect(() => {
    if (!jobs) return;

    const processing = jobs.reduce((n, j) =>
      n + j.items.filter(i => i.status === "queued" || i.status === "processing").length, 0);
    const ready = jobs.reduce((n, j) => n + j.items.filter(i => i.status === "ready").length, 0);
    setPersistentToast(processing > 0
      ? `${processing} ad${processing === 1 ? "" : "s"} processing · ${ready} ready`
      : null);

    for (const job of jobs) {
      const prev = lastSeenStatus.current.get(job.job_id);
      // Only alert on a transition OBSERVED within this session (prev is a known non-terminal
      // status) — a job that's already ready/partial/error on the very first poll after mount is
      // pre-existing pending-review state from before this page load, not something that "just"
      // finished, so it must not re-toast on every refresh.
      const justFinished = (prev === "queued" || prev === "processing")
        && (job.status === "ready" || job.status === "partial" || job.status === "error");
      if (justFinished) {
        if (job.status === "ready") {
          pushToast("Your ads are ready to review");
        } else if (job.status === "partial") {
          const failed = job.items.filter(i => i.status === "error").length;
          pushToast(`${job.item_count - failed} ad${job.item_count - failed === 1 ? "" : "s"} ready, `
            + `${failed} failed — review to retry`, "error");
        } else {
          pushToast("Ad copy generation failed — review to retry", "error");
        }
      }
      lastSeenStatus.current.set(job.job_id, job.status);
    }
    // Drop bookkeeping for jobs that no longer appear (consumed elsewhere), so a job_id reused far in
    // the future can't spuriously read as "already seen".
    const stillActive = new Set(jobs.map(j => j.job_id));
    for (const id of lastSeenStatus.current.keys()) {
      if (!stillActive.has(id)) lastSeenStatus.current.delete(id);
    }
  }, [jobs, pushToast, setPersistentToast]);
}
